import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Label } from 'recharts';

function WeatherChart ({
  data,
  startDate,
  endDate,
  interval,
  unitSettings,
  calculateDomain,
  chartConfig = {},
}) {
  if (!data || data.length === 0) {
    return <p>No data available for this chart.</p>;
  }

  const { 
    dataKey = '', 
    minKey = '', 
    maxKey = '', 
    color = '#8884d8', 
    fillId = 'defaultFill', 
    domainMargin = 5, 
    yAxisLabel = '' 
  } = chartConfig;

  if (!dataKey || !minKey || !maxKey) {
    return <p>Invalid chart configuration.</p>;
  }

  const rawDomain = calculateDomain(data, minKey, maxKey, domainMargin);

  // snap to integers...
  let minY = Math.floor(rawDomain[0]);
  const maxY = Math.ceil(rawDomain[1]);

  // solar radiation should never go below zero, special case
  if (dataKey === 'watts') {
    minY = 0;
  }

  const domain = [minY, maxY];

  // Determine if we are showing an hourly forecast (less than one day)
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const forecastIsHourly = (new Date(endDate) - new Date(startDate)) <= ONE_DAY;

  // Format X-axis ticks: hourly => 0-23h, daily/weekly => date format
  const formatDateTick = (value) => {
    const date = new Date(Number(value));
    if (forecastIsHourly) {
      const h = date.getHours();
      if (h % 2 !== 0) return '';
      const suffix = h < 12 ? 'AM' : 'PM';
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      return `${hour12}${suffix}`;
    }
    // fallback for daily/weekly:
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const yAxisTickFormatter = (value) => Number(value).toFixed(0);

  return (
    <ResponsiveContainer width="100%" height={800}>
      <AreaChart data={data} margin={{ top: 16, right: 32, left: 8, bottom: 16 }}>
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={1} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>

        <XAxis
          dataKey="date"
          axisLine={false}
          interval={interval}
          tickCount={8}
          tickFormatter={formatDateTick}
        />

        <YAxis
          yAxisId="left"
          domain={domain} 
          axisLine={false}
          tickFormatter={yAxisTickFormatter}
          width={100}
          tickMargin={10}
        >
          <Label
            value={
              typeof yAxisLabel === 'function'
                ? yAxisLabel(unitSettings)
                : yAxisLabel
            }
            angle={-90}
            position="insideLeft"
            style={{ textAnchor: 'middle' }}
          />
        </YAxis>

        <Tooltip
          formatter={(value) => Number(value).toFixed(2)}
          labelFormatter={(label) =>
            new Date(Number(label)).toLocaleString(undefined, {
              month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric'
            })
          }
        />

        <Area
          yAxisId="left"
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          fillOpacity={1}
          fill={`url(#${fillId})`}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default WeatherChart;
